import { getCount, getResource, readUrl, updateResource } from "./api";

const UNREAD_PITCH_SCAN_LIMIT = 1000;
const ORIGIN_LOOKUP_CHUNK = 200;
const INBOX_UNREAD_CHANGED_EVENT = "qwoted:inbox-unread-changed";

// Unread pitches the Everything feed shows as pitch rows: sent on the receiver's requests and
// not yet replied to. A replied pitch surfaces as a conversation and is counted with those.
const countUnreadPitchRows = async (receiverProfileId) => {
  const pitchSelector =
    `select=id&request_media_person_id=eq.${receiverProfileId}` +
    `&sent_at=not.is.null&read_at=is.null&limit=${UNREAD_PITCH_SCAN_LIMIT}`;
  const { data: pitches, error } = await getResource(readUrl("pitches", { selector: pitchSelector }));
  if (error) return 0;

  const pitchIds = (pitches || []).map((pitch) => pitch.id);
  if (pitchIds.length === 0) return 0;

  const chunks = [];
  for (let i = 0; i < pitchIds.length; i += ORIGIN_LOOKUP_CHUNK) {
    chunks.push(pitchIds.slice(i, i + ORIGIN_LOOKUP_CHUNK));
  }
  const lookups = await Promise.all(
    chunks.map((chunk) => {
      const selector =
        `select=origin_id&receiver_profile_id=eq.${receiverProfileId}` +
        `&origin_type=eq.Interact::Pitch&origin_id=in.(${chunk.join(",")})`;
      return getResource(readUrl("conversations", { selector }));
    }),
  );
  if (lookups.some((lookup) => lookup.error)) return 0;

  const replied = new Set(lookups.flatMap((lookup) => (lookup.data || []).map((row) => row.origin_id)));
  return pitchIds.length - replied.size;
};

const unreadConversationsCount = (receiverProfileId, { focusedOnly = false } = {}) => {
  const focusedFilter = focusedOnly ? "&conversation_mailbox=eq.focused" : "";
  const selector = `receiver_profile_id=eq.${receiverProfileId}&receipt_read_at=is.null&receipt_archived_at=is.null${focusedFilter}`;
  return getCount(readUrl("conversations", { selector }));
};

/**
 * The navbar inbox indicator counts what the user's primary tab badges: unread conversations in
 * Focused for media users, in the whole inbox for promoters (who have no Focused tab). Unread
 * pitches never count here — they stay in the Everything badge only (product call, 2026-09-10).
 *
 * @param {number} receiverProfileId - The receiver's numeric profile id
 * @param {boolean} options.isMedia - Media users count Focused only
 * @returns {Promise<number>}
 */
const getNavbarUnreadCount = (receiverProfileId, { isMedia = true } = {}) =>
  unreadConversationsCount(receiverProfileId, { focusedOnly: isMedia });

/**
 * Get mailbox counts for inbox tabs.
 *
 * - `focused` and `everything` are UNREAD counts (receipt_read_at is null, not archived).
 *   Media users badge Focused with `focused`; promoters badge Inbox with `everything`.
 *   With `includePitches`, `everything` also counts the unread pitch rows the Everything feed
 *   shows to media users.
 * - `trash` is a TOTAL count of archived (trashed) conversations, regardless of read state.
 *   Rendered as a plain text indicator on the Trash tab, always visible (including 0).
 *
 * @param {number} receiverProfileId - The receiver's numeric profile id (stable across renames; slug is not)
 * @param {boolean} options.includePitches - Add unread, unreplied pitches on the receiver's requests to `everything`
 * @returns {Promise<{focused: number, everything: number, trash: number}>}
 */
const getUnreadCounts = async (receiverProfileId, { includePitches = false } = {}) => {
  const trashUrl = readUrl("conversations", {
    selector: `receiver_profile_id=eq.${receiverProfileId}&receipt_archived_at=not.is.null`,
  });

  const [focused, conversations, trash, unreadPitches] = await Promise.all([
    unreadConversationsCount(receiverProfileId, { focusedOnly: true }),
    unreadConversationsCount(receiverProfileId),
    getCount(trashUrl),
    includePitches ? countUnreadPitchRows(receiverProfileId) : 0,
  ]);

  return { focused, everything: conversations + unreadPitches, trash };
};

// The inbox marks things read in-page, but the navbar indicator is its own React root, so the
// inbox announces its counts through a window event.
const notifyInboxUnreadChanged = (counts) => {
  if (typeof window === "undefined" || !window.dispatchEvent) return;
  const detail = { focused: counts.focused, everything: counts.everything };
  window.dispatchEvent(new CustomEvent(INBOX_UNREAD_CHANGED_EVENT, { detail }));
};

// `handler` receives the announced `{ focused, everything }` counts. Returns an unsubscribe function.
const onInboxUnreadChanged = (handler) => {
  if (typeof window === "undefined" || !window.addEventListener) return () => {};
  const listener = (event) => {
    const counts = event.detail;
    if (counts && typeof counts.focused === "number" && typeof counts.everything === "number") handler(counts);
  };
  window.addEventListener(INBOX_UNREAD_CHANGED_EVENT, listener);
  return () => window.removeEventListener(INBOX_UNREAD_CHANGED_EVENT, listener);
};

/**
 * Move a conversation to a different mailbox.
 * "focused" = appears in the Focused tab; "other" = appears only in Everything.
 * @param {string} conversationId - The conversation UUID
 * @param {string} mailbox - Target mailbox: "focused" or "other"
 * @returns {Promise<{data: object} | {error: object}>}
 */
const updateMailbox = async (conversationId, mailbox) => {
  const url = `/interact/api/messaging/conversations/${conversationId}/mailbox`;

  return updateResource(url, {
    data: {
      attributes: {
        mailbox,
      },
    },
  });
};

export default {
  getNavbarUnreadCount,
  getUnreadCounts,
  notifyInboxUnreadChanged,
  onInboxUnreadChanged,
  updateMailbox,
};
